import type { IconDecorationProtocol } from '@lighthouse/core'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { AudioStatusIcon } from './AudioStatusIcon'
import { formatVoiceDuration } from './format'

interface VoiceButtonProps {
    playing: boolean
    duration: number
    showDuration: boolean
    error: boolean
    disabled?: boolean
    icon?: IconDecorationProtocol
    iconColor?: string
    onGetFullUrl?: (url: string) => string
    onToggle: () => void
}

const Root = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 100%;
    padding: 0 8px;
    border: none;
    border-radius: 999px;
    background: var(--color-gray-100, #f2f4f7);
    color: var(--color-gray-800, #1d2939);
    cursor: pointer;
    user-select: none;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`

const Duration = styled.span`
    font-size: 12px;
    line-height: 16px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`

export const VoiceButton: React.FC<VoiceButtonProps> = ({
    playing,
    duration,
    showDuration,
    error,
    disabled,
    icon,
    iconColor,
    onGetFullUrl,
    onToggle
}) => {
    const [frame, setFrame] = useState<0 | 1 | 2>(0)

    useEffect(() => {
        if (!playing) {
            setFrame(0)
            return
        }
        const timer = window.setInterval(() => {
            setFrame(prev => ((prev + 1) % 3) as 0 | 1 | 2)
        }, 280)
        return () => {
            window.clearInterval(timer)
        }
    }, [playing])

    return (
        <Root type="button" disabled={disabled || error} onClick={onToggle} data-stop-action-propagation>
            <AudioStatusIcon
                playing={playing}
                icon={icon}
                color={iconColor}
                size={icon?.value ? 14 : playing ? 16 : 14}
                playingFallback="wave"
                waveFrame={frame}
                onGetFullUrl={onGetFullUrl}
            />
            {showDuration && <Duration>{formatVoiceDuration(duration, error)}</Duration>}
        </Root>
    )
}
